// ─── MITREMatrix.jsx ─────────────────────────────────────────────────────────
// Full MITRE ATT&CK matrix showing observed tactics highlighted.
// Toggled from the incident detail panel.
//
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState } from 'react';

const mono = { fontFamily: 'JetBrains Mono, monospace' };

// Full 14-tactic ATT&CK Enterprise matrix (trimmed to most relevant techniques)
const MATRIX = [
  { id: 'TA0043', name: 'Reconnaissance',     techniques: ['T1595','T1592','T1589','T1590','T1591','T1598'] },
  { id: 'TA0042', name: 'Resource Dev',       techniques: ['T1583','T1584','T1585','T1586','T1587','T1588'] },
  { id: 'TA0001', name: 'Initial Access',     techniques: ['T1190','T1133','T1200','T1091','T1195','T1566'] },
  { id: 'TA0002', name: 'Execution',          techniques: ['T1059','T1203','T1559','T1106','T1053','T1129'] },
  { id: 'TA0003', name: 'Persistence',        techniques: ['T1098','T1136','T1543','T1546','T1547','T1574'] },
  { id: 'TA0004', name: 'Priv Escalation',   techniques: ['T1548','T1134','T1484','T1611','T1543','T1574'] },
  { id: 'TA0005', name: 'Defense Evasion',    techniques: ['T1140','T1222','T1070','T1036','T1027','T1055'] },
  { id: 'TA0006', name: 'Cred Access',        techniques: ['T1110','T1555','T1212','T1187','T1606','T1539'] },
  { id: 'TA0007', name: 'Discovery',          techniques: ['T1087','T1010','T1217','T1613','T1083','T1046'] },
  { id: 'TA0008', name: 'Lateral Movement',   techniques: ['T1210','T1534','T1570','T1563','T1021','T1080'] },
  { id: 'TA0009', name: 'Collection',         techniques: ['T1560','T1123','T1119','T1115','T1213','T1005'] },
  { id: 'TA0011', name: 'C2',                 techniques: ['T1071','T1132','T1001','T1568','T1573','T1008'] },
  { id: 'TA0010', name: 'Exfiltration',       techniques: ['T1020','T1030','T1048','T1041','T1011','T1052'] },
  { id: 'TA0040', name: 'Impact',             techniques: ['T1485','T1486','T1491','T1561','T1529','T1565'] },
];

// Map your attack types → MITRE tactic IDs
const APT_TO_TACTICS = {
  'APT-Reconnaissance':       ['TA0043'],
  'APT-Living-Off-The-Land':  ['TA0002'],
  'APT-Privilege-Escalation': ['TA0004'],
  'APT-Persistence':          ['TA0003'],
  'APT-Lateral-Movement':     ['TA0008'],
  'APT-Defense-Evasion':      ['TA0005'],
  'APT-C2-Communication':     ['TA0011'],
  'APT-Data-Exfiltration':    ['TA0010'],
};

export default function MITREMatrix({ attackTypes = [] }) {
  const [hoveredTactic, setHoveredTactic] = useState(null);

  const observedTactics = new Set(
    attackTypes.flatMap(at => APT_TO_TACTICS[at] || [])
  );

  return (
    <div style={{ background: '#0d1018', border: '1px solid #ffffff0a', borderRadius: 6, padding: 12, marginTop: 10 }}>
      <div style={{ fontSize: 10, color: '#4b5563', ...mono, marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>MITRE ATT&CK ENTERPRISE MATRIX</span>
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div style={{ width: 8, height: 8, background: '#ff3b4e44', border: '1px solid #ff3b4e88', borderRadius: 2 }} />
            <span style={{ color: '#6b7280' }}>Observed in incident</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div style={{ width: 8, height: 8, background: '#ffffff08', border: '1px solid #ffffff14', borderRadius: 2 }} />
            <span style={{ color: '#6b7280' }}>Not observed</span>
          </div>
        </div>
      </div>

      {/* Tactic columns */}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${MATRIX.length}, 1fr)`, gap: 4, overflowX: 'auto' }}>
        {MATRIX.map(tactic => {
          const isObserved = observedTactics.has(tactic.id);
          const col = isObserved ? '#ff3b4e' : '#ffffff14';
          const bg  = isObserved ? '#ff3b4e18' : '#ffffff04';
          return (
            <div key={tactic.id}
              onMouseEnter={() => setHoveredTactic(tactic.id)}
              onMouseLeave={() => setHoveredTactic(null)}
              style={{ minWidth: 64 }}>
              {/* Tactic header */}
              <div style={{
                background: isObserved ? '#ff3b4e22' : '#141720',
                border: `1px solid ${col}`,
                borderRadius: '4px 4px 0 0',
                padding: '4px 5px',
                textAlign: 'center',
              }}>
                <div style={{ fontSize: 8, color: isObserved ? '#ff6b7a' : '#4b5563', ...mono }}>{tactic.id}</div>
                <div style={{ fontSize: 9, color: isObserved ? '#e8eaf0' : '#6b7280', ...mono, fontWeight: isObserved ? 600 : 400, marginTop: 2, lineHeight: 1.2 }}>
                  {tactic.name}
                </div>
              </div>
              {/* Technique cells */}
              {tactic.techniques.map((t, i) => (
                <div key={t} style={{
                  background: isObserved ? bg : '#0d1018',
                  border: `1px solid ${isObserved ? '#ff3b4e22' : '#ffffff08'}`,
                  borderTop: 'none',
                  padding: '3px 5px',
                  borderRadius: i === tactic.techniques.length - 1 ? '0 0 4px 4px' : 0,
                }}>
                  <div style={{ fontSize: 8, color: isObserved ? '#ff6b7a88' : '#374151', ...mono }}>{t}</div>
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {observedTactics.size > 0 && (
        <div style={{ marginTop: 10, padding: '6px 10px', background: '#ff3b4e0a', borderRadius: 4, fontSize: 10, color: '#ff6b7a', ...mono, border: '1px solid #ff3b4e22' }}>
          {observedTactics.size} of {MATRIX.length} ATT&CK tactics detected in this incident
        </div>
      )}
    </div>
  );
}
